import { Link } from 'react-router-dom'
import { Search, ArrowRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui'
import { formatPriceCents, getBrands, getGroupedBrands } from '@/data/brands'
import { useSeo } from '@/lib/seo'

/** Image for a brand card: uses /brand-images/<slug>.jpg when available, else first letter. */
function BrandCardImage({ slug, name }: { slug: string; name: string }) {
  const [failed, setFailed] = useState(false)
  // Must NOT live under `/brands/` path (router conflict). Use a non-route static path.
  const jpgSrc = `/brand-images/${slug}.jpg`
  const webpSrc = `/brand-images/${slug}.webp`
  if (failed) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-6xl font-headline font-light text-navy-900/10 group-hover:scale-110 transition-transform duration-500">
          {name.charAt(0)}
        </span>
      </div>
    )
  }
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        src={jpgSrc}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </picture>
  )
}

export function BrandsPage() {
  const [query, setQuery] = useState('')
  const brands = getBrands()

  useSeo({
    title: 'Brands | Bespokian Auto',
    description: 'Browse certified pre-owned brands and explore models, pricing, and buyer guides.',
    // Use homepage OG image as a safe default for hub pages
    image: '/img/og/home-og.jpg',
  })

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return brands
    return brands.filter((b) => b.name.toLowerCase().includes(q) || b.slug.includes(q))
  }, [brands, query])

  const grouped = useMemo(() => {
    // Preserve the client's category grouping, but apply search filtering.
    const filteredSlugs = new Set(filtered.map((b) => b.slug))
    return getGroupedBrands()
      .map((g) => ({ ...g, brands: g.brands.filter((b) => filteredSlugs.has(b.slug)) }))
      .filter((g) => g.brands.length > 0)
  }, [filtered])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Immersive Style */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1920&q=80')`
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/35" />
        
        {/* Content - centered in viewport */}
        <div className="relative z-10 w-full flex flex-col items-center justify-center px-6">
          <div className="text-center max-w-4xl w-full">
            <h1 className="font-display text-5xl md:text-6xl text-white leading-tight tracking-tight">
              Discover Your Drive
            </h1>
          </div>

          {/* Search Form */}
          <div className="mt-10 w-full max-w-2xl">
            <form className="w-full" onSubmit={(e) => e.preventDefault()}>
              <div className="w-full">
                <div 
                  className="backdrop-blur-md rounded-lg px-6 py-4 shadow-xl w-full border border-white/20"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.21)' }}
                >
                  <label 
                    htmlFor="brand-search" 
                    className="block text-xs uppercase tracking-widest text-white mb-3 text-left font-medium"
                  >
                    Which brand are you looking for?
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      id="brand-search"
                      type="text"
                      placeholder="Search brands..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="flex-1 text-base text-white placeholder:text-white/70 bg-transparent border-none outline-none focus:outline-none"
                    />
                    <button 
                      type="submit"
                      className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-md"
                    >
                      <Search className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Brand Categories */}
      {grouped.map((group) => (
        <section key={group.label} className="py-section-lg px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Category Header */}
            <div className="mb-10">
              <p className="text-sm font-medium tracking-widest text-muted-navy uppercase mb-2">
                {group.label}
              </p>
              <p className="text-muted-navy">
                {group.brands.length} brands
              </p>
            </div>

            {/* Brand Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {group.brands.map((brand) => (
                <Link
                  key={brand.slug}
                  to={`/brands/${brand.slug}`}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl"
                >
                  {/* Image Area */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-navy-900/20 to-transparent" />
                    {/* Brand image from frontend/public/brands/<slug>.jpg (see img/fetch_brand_images.py) */}
                    <BrandCardImage slug={brand.slug} name={brand.name} />
                    {/* Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge variant="secondary" className="bg-white/90 text-navy-900 backdrop-blur-sm">
                        {brand.modelCount} models
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors">
                      {brand.name}
                    </h3>
                    <p className="mt-1 text-sm font-medium tracking-wide text-muted-navy uppercase">
                      {brand.tagline}
                    </p>
                    <p className="mt-4 text-muted-navy text-sm leading-relaxed line-clamp-3">
                      {brand.description}
                    </p>

                    {/* Popular Models */}
                    <div className="mt-5 flex flex-wrap gap-2">
                      {brand.popularModels.slice(0, 3).map((m) => (
                        <span
                          key={m.slug}
                          className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy"
                          title={`Starting around ${formatPriceCents(m.startingPrice)}`}
                        >
                          {m.name}
                        </span>
                      ))}
                      {brand.popularModels.length > 3 && (
                        <span className="text-xs px-3 py-1 rounded-full bg-gray-100 text-muted-navy">
                          +{brand.popularModels.length - 3} more
                        </span>
                      )}
                    </div>

                    {/* Explore Link */}
                    <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
                      <span>Explore</span>
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  )
}
